import React, { useEffect, useState } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ImageIcon, Calculator, Package, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';
import SeleccionarClienteInput from './SeleccionarClienteInput';
import CotizacionPuertaPuertaImagenModal from './CotizacionPuertaPuertaImagenModal';
import PaqueteFormModal from './PaqueteFormModal';
import NuevaSolicitudPuertaPuertaAdminModal from './NuevaSolicitudPuertaPuertaAdminModal';

const ESTADO_LABEL = {
  nueva: 'Nueva',
  cotizando: 'Cotizando',
  cotizacion_enviada: 'Cotización enviada',
  confirmada: 'Confirmada',
  rechazada: 'Rechazada',
  sin_respuesta: 'Sin respuesta',
};

const ESTADO_COLORS = {
  nueva: { bg: '#E3F2FD', color: '#1565C0' },
  cotizando: { bg: '#FFF3E0', color: '#E65100' },
  cotizacion_enviada: { bg: '#FFF9C4', color: '#F57F17' },
  confirmada: { bg: '#E8F5E9', color: '#2E7D32' },
  rechazada: { bg: '#FFEBEE', color: '#C62828' },
  sin_respuesta: { bg: '#FAFAFA', color: '#9E9E9E' },
};

const PRODUCTOS_LABEL = {
  electronicos: 'Electrónicos', baterias: 'Baterías', liquidos: 'Líquidos',
  cosmeticos: 'Cosméticos o perfumes', medicinas: 'Medicinas o suplementos',
  alimentos: 'Alimentos', repuestos: 'Repuestos',
};

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-EC', {
    day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function PuertaPuertaPanel() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [pagina, setPagina] = useState(1);
  const [porPagina] = useState(20);
  const [expandido, setExpandido] = useState(null);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [notasTemp, setNotasTemp] = useState({});
  const [vinculando, setVinculando] = useState(null);
  const [cotizarTemp, setCotizarTemp] = useState({}); // { solicitudId: { ups_ground, pickup_estimado } }
  const [cotizando, setCotizando] = useState(null);
  const [mostrarImagen, setMostrarImagen] = useState(null); // id de la solicitud
  const [creandoPaquete, setCreandoPaquete] = useState(null); // id de la solicitud
  const [creandoManual, setCreandoManual] = useState(false);

  const cargar = () => {
    setCargando(true);
    setSeleccionados(new Set());
    const params = { ordering: '-creado' };
    if (filtro) params.search = filtro;
    if (filtroEstado) params.estado = filtroEstado;
    api.get('/puerta-a-puerta/', { params })
      .then((res) => setSolicitudes(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar las solicitudes.'))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => setFiltro(busqueda.trim()), 350);
    return () => clearTimeout(timer);
  }, [busqueda]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [filtro, filtroEstado]);
  useEffect(() => setPagina(1), [filtro, filtroEstado]);

  const totalPaginas = Math.max(1, Math.ceil(solicitudes.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const paginadas = solicitudes.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  const toggleSeleccion = (id) => setSeleccionados((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleTodosPagina = () => {
    const ids = paginadas.map((s) => s.id);
    const todos = ids.length > 0 && ids.every((id) => seleccionados.has(id));
    setSeleccionados((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (todos ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const eliminarSeleccionados = async () => {
    const ids = Array.from(seleccionados);
    const conPago = solicitudes.filter((s) => seleccionados.has(s.id) && s.pagado).length;
    const aviso = conPago > 0 ? ` OJO: ${conPago} tiene un pago registrado y ese registro se perderá.` : '';
    if (!window.confirm(`¿Eliminar ${ids.length} solicitud(es)? No se puede deshacer.${aviso}`)) return;
    try {
      await api.post('/puerta-a-puerta/eliminar-lote/', { ids });
      toast.success(`${ids.length} solicitud(es) eliminada(s).`);
      cargar();
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudieron eliminar.'));
    }
  };

  const resumen = {
    total: solicitudes.length,
    nuevas: solicitudes.filter((s) => s.estado === 'nueva').length,
    cotizando: solicitudes.filter((s) => s.estado === 'cotizando').length,
    confirmadas: solicitudes.filter((s) => s.estado === 'confirmada').length,
  };

  const cambiarEstado = async (id, nuevoEstado) => {
    try {
      const res = await api.patch(`/puerta-a-puerta/${id}/`, { estado: nuevoEstado });
      setSolicitudes((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      toast.success('Estado actualizado.');
    } catch {
      toast.error('No se pudo actualizar el estado.');
    }
  };

  const guardarNotas = async (id) => {
    try {
      const res = await api.patch(`/puerta-a-puerta/${id}/`, { notas_internas: notasTemp[id] ?? '' });
      setSolicitudes((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      toast.success('Notas guardadas.');
    } catch {
      toast.error('No se pudieron guardar las notas.');
    }
  };

  const vincularCliente = async (id, cliente) => {
    try {
      const res = await api.post(`/puerta-a-puerta/${id}/vincular_cliente/`, { cliente_id: cliente.id });
      setSolicitudes((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      toast.success('Cliente vinculado.');
    } catch {
      toast.error('No se pudo vincular el cliente.');
    } finally {
      setVinculando(null);
    }
  };

  const toggleExpandir = (s) => {
    if (expandido === s.id) {
      setExpandido(null);
    } else {
      setExpandido(s.id);
      setNotasTemp((prev) => ({ ...prev, [s.id]: prev[s.id] ?? (s.notas_internas || '') }));
      setCotizarTemp((prev) => ({
        ...prev,
        [s.id]: prev[s.id] ?? { ups_ground: s.ups_ground ?? '', pickup_estimado: s.pickup_estimado ?? '' },
      }));
    }
  };

  const actualizarCotizarTemp = (id, campo, valor) => {
    setCotizarTemp((prev) => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));
  };

  const cotizar = async (s) => {
    const datos = cotizarTemp[s.id] || {};
    if (!datos.ups_ground) {
      toast.error('Ingresa el costo de UPS Ground.');
      return;
    }
    setCotizando(s.id);
    try {
      const res = await api.post(`/puerta-a-puerta/${s.id}/cotizar/`, {
        ups_ground: datos.ups_ground,
        pickup_estimado: datos.pickup_estimado || 0,
      });
      setSolicitudes((prev) => prev.map((x) => (x.id === s.id ? res.data : x)));
      toast.success('Cotización calculada.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo calcular la cotización.');
    } finally {
      setCotizando(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
        <h2 style={{ marginBottom: 0 }}>Puerta a Puerta</h2>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button className="btn-secondary btn-sm" onClick={() => setCreandoManual(true)}>
            <Plus size={14} /> Nueva solicitud
          </button>
          <button className="btn-ghost btn-sm" onClick={cargar}><RefreshCw size={14} /> Actualizar</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
        <div className="card" style={{ padding: 'var(--spacing-3)' }}>
          <div className="text-xs text-secondary">Total</div>
          <div style={{ fontSize: '24px', fontWeight: 700 }}>{resumen.total}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)' }}>
          <div className="text-xs text-secondary">Nuevas</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1565C0' }}>{resumen.nuevas}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)' }}>
          <div className="text-xs text-secondary">Cotizando</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#E65100' }}>{resumen.cotizando}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)' }}>
          <div className="text-xs text-secondary">Confirmadas</div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#2E7D32' }}>{resumen.confirmadas}</div>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 'var(--spacing-3)' }}>
        <div className="toolbar__buscar">
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
          <input
            placeholder="Buscar por nombre o correo..."
            value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            style={{ paddingLeft: '32px', width: '100%' }}
          />
        </div>
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} style={{ minWidth: '160px' }}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {seleccionados.size > 0 && (
            <button className="btn-ghost btn-sm" onClick={eliminarSeleccionados} style={{ color: '#D32F2F', whiteSpace: 'nowrap' }}>
              <Trash2 size={14} /> Eliminar ({seleccionados.size})
            </button>
          )}
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36px' }}>
                <input
                  type="checkbox"
                  aria-label="Seleccionar todas"
                  checked={paginadas.length > 0 && paginadas.every((s) => seleccionados.has(s.id))}
                  onChange={toggleTodosPagina}
                />
              </th>
              <th style={{ width: '36px' }}></th>
              <th>Fecha</th>
              <th>Nombre</th>
              <th>Email / WhatsApp</th>
              <th>Origen</th>
              <th>Destino</th>
              <th>Peso</th>
              <th>Cliente</th>
              <th>Cotización</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={11} className="text-secondary">Cargando...</td></tr>
            ) : solicitudes.length === 0 ? (
              <tr><td colSpan={11} className="text-secondary">No hay solicitudes registradas.</td></tr>
            ) : paginadas.map((s) => {
              const ec = ESTADO_COLORS[s.estado] || ESTADO_COLORS.nueva;
              const isOpen = expandido === s.id;
              return (
                <React.Fragment key={s.id}>
                  <tr style={{ cursor: 'pointer', background: isOpen ? 'var(--color-bg-secondary)' : undefined }} onClick={() => toggleExpandir(s)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={seleccionados.has(s.id)} onChange={() => toggleSeleccion(s.id)} />
                    </td>
                    <td>{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                    <td className="text-sm text-secondary" style={{ whiteSpace: 'nowrap' }}>{formatFecha(s.creado)}</td>
                    <td style={{ fontWeight: 600 }}>{s.nombre_completo}</td>
                    <td className="text-sm">
                      <div>{s.email}</div>
                      <div className="text-secondary">{s.whatsapp}</div>
                    </td>
                    <td className="text-sm">{s.ciudad_origen}{s.estado_usa ? `, ${s.estado_usa}` : ''}</td>
                    <td className="text-sm">{s.ciudad_destino_ec}{s.provincia_ec ? `, ${s.provincia_ec}` : ''}</td>
                    <td className="text-sm mono">{s.peso_lb ? `${s.peso_lb} lb` : '—'}</td>
                    <td className="text-sm">
                      {s.cliente_detalle ? (
                        <span className="mono">{s.cliente_detalle.codigo_casillero}</span>
                      ) : (
                        <span className="text-secondary">Sin vincular</span>
                      )}
                    </td>
                    <td className="text-sm mono" style={{ fontWeight: 600 }}>
                      {s.cotizacion_cliente ? `$${Number(s.cotizacion_cliente).toFixed(2)}` : '—'}
                      {s.pagado && <span title="Pagado" style={{ color: '#2E7D32', marginLeft: '4px' }}>✓</span>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        value={s.estado}
                        onChange={(e) => cambiarEstado(s.id, e.target.value)}
                        style={{
                          background: ec.bg, color: ec.color, border: 'none', borderRadius: '6px',
                          padding: '4px 8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', minWidth: '150px',
                        }}
                      >
                        {Object.entries(ESTADO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={11} style={{ padding: 'var(--spacing-4)', background: 'var(--color-bg-secondary)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--spacing-4)' }}>
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Paquete</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                              <span>Medidas:</span><span style={{ color: 'var(--color-text)' }}>{s.largo || '—'} x {s.ancho || '—'} x {s.alto || '—'} in</span>
                              <span>Paquetes:</span><span style={{ color: 'var(--color-text)' }}>{s.num_paquetes}</span>
                              <span>¿Listo para enviar?:</span><span style={{ color: 'var(--color-text)' }}>{s.paquete_listo === 'si' ? 'Sí' : s.paquete_listo === 'no' ? 'No' : s.paquete_listo || '—'}</span>
                              <span>Contenido:</span><span style={{ color: 'var(--color-text)' }}>{s.contenido_descripcion || '—'}</span>
                              <span>Valor declarado:</span><span style={{ color: 'var(--color-text)' }}>{s.valor_declarado ? `$${Number(s.valor_declarado).toFixed(2)}` : '—'}</span>
                              <span>Condición:</span><span style={{ color: 'var(--color-text)' }}>{s.condicion || '—'}</span>
                              <span>Productos especiales:</span>
                              <span style={{ color: 'var(--color-text)' }}>
                                {(s.productos_especiales || []).length > 0
                                  ? s.productos_especiales.map((p) => PRODUCTOS_LABEL[p] || p).join(', ')
                                  : 'Ninguno declarado'}
                              </span>
                            </div>
                            {s.foto && (
                              <a href={s.foto} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm" style={{ marginTop: 'var(--spacing-2)', display: 'inline-flex' }}>
                                <ImageIcon size={14} /> Ver foto del paquete
                              </a>
                            )}

                            <h4 style={{ margin: 'var(--spacing-3) 0', fontSize: '13px' }}>Entrega</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                              <span>Retiro en USA:</span><span style={{ color: 'var(--color-text)' }}>{s.entrega_preferencia === 'domicilio' ? 'A domicilio' : s.entrega_preferencia === 'punto_ups' ? 'Cliente entrega en punto UPS' : s.entrega_preferencia || '—'}</span>
                              <span>Dirección USA:</span><span style={{ color: 'var(--color-text)' }}>{s.direccion_retiro_usa || '—'}</span>
                              <span>ZIP origen:</span><span style={{ color: 'var(--color-text)' }}>{s.zip_code || '—'}</span>
                              <span>Entrega a domicilio EC:</span><span style={{ color: 'var(--color-text)' }}>{s.entrega_domicilio_ec === 'si' ? 'Sí' : s.entrega_domicilio_ec === 'no' ? 'No, retiro en oficina' : s.entrega_domicilio_ec || '—'}</span>
                              <span>Dirección EC:</span><span style={{ color: 'var(--color-text)' }}>{s.direccion_entrega_ec || '—'}</span>
                            </div>

                            <h4 style={{ margin: 'var(--spacing-3) 0', fontSize: '13px' }}>Cotización</h4>
                            <p className="text-xs text-secondary" style={{ marginTop: 0, marginBottom: 'var(--spacing-2)' }}>
                              Saca estos dos valores del cotizador de tu cuenta UPS — el resto se calcula solo.
                            </p>
                            <div className="form-row">
                              <input
                                type="number" step="0.01" min="0" placeholder="UPS Ground *"
                                value={cotizarTemp[s.id]?.ups_ground ?? ''}
                                onChange={(e) => actualizarCotizarTemp(s.id, 'ups_ground', e.target.value)}
                                style={{ fontSize: '12px' }}
                              />
                              <input
                                type="number" step="0.01" min="0" placeholder="Pickup estimado"
                                value={cotizarTemp[s.id]?.pickup_estimado ?? ''}
                                onChange={(e) => actualizarCotizarTemp(s.id, 'pickup_estimado', e.target.value)}
                                style={{ fontSize: '12px' }}
                              />
                            </div>
                            <button className="btn-primary btn-sm" style={{ marginTop: 'var(--spacing-2)' }}
                              onClick={() => cotizar(s)} disabled={cotizando === s.id}>
                              <Calculator size={14} /> {cotizando === s.id ? 'Calculando...' : 'Calcular cotización'}
                            </button>

                            {cotizando === s.id ? (
                              <p className="text-sm text-secondary" style={{ marginTop: 'var(--spacing-3)' }}>Calculando...</p>
                            ) : s.cotizacion_cliente && (
                              <div className="card" style={{ padding: 'var(--spacing-3)', marginTop: 'var(--spacing-3)' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '12px' }}>
                                  <span className="text-secondary">UPS Ground:</span><span className="mono text-right">${Number(s.ups_ground).toFixed(2)}</span>
                                  <span className="text-secondary">Pickup estimado:</span><span className="mono text-right">${Number(s.pickup_estimado).toFixed(2)}</span>
                                  <span className="text-secondary">Fee Ecuafly:</span><span className="mono text-right">${Number(s.fee_ecuafly).toFixed(2)}</span>
                                  <span className="text-secondary">Reserva operativa:</span><span className="mono text-right">${Number(s.reserva_operativa).toFixed(2)}</span>
                                  <span style={{ fontWeight: 700 }}>Total a cobrar:</span><span className="mono text-right" style={{ fontWeight: 700 }}>${Number(s.cotizacion_cliente).toFixed(2)}</span>
                                  <span className="text-secondary" style={{ gridColumn: '1 / -1', marginTop: '4px', borderTop: '1px solid var(--color-border)', paddingTop: '4px' }}>Interno — nunca visible al cliente:</span>
                                  <span className="text-secondary">Comisión Payphone:</span><span className="mono text-right text-secondary">${Number(s.comision_payphone).toFixed(2)}</span>
                                  <span className="text-secondary">Utilidad Ecuafly:</span><span className="mono text-right text-secondary">${Number(s.utilidad_ecuafly).toFixed(2)}</span>
                                </div>
                                {s.pagado ? (
                                  <p className="text-xs" style={{ margin: '8px 0 0', color: '#2E7D32', fontWeight: 600 }}>
                                    ✓ Pagado{s.payphone_tarjeta ? ` con ${s.payphone_tarjeta}` : ''}
                                  </p>
                                ) : (
                                  <button className="btn-secondary btn-sm" style={{ marginTop: 'var(--spacing-3)', width: '100%' }}
                                    onClick={() => setMostrarImagen(s.id)}>
                                    <ImageIcon size={14} /> Generar imagen para enviar
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Cliente</h4>
                            {s.cliente_detalle ? (
                              <p className="text-sm">
                                <span className="mono">{s.cliente_detalle.codigo_casillero}</span> — {s.cliente_detalle.nombre}
                              </p>
                            ) : vinculando === s.id ? (
                              <SeleccionarClienteInput seleccionado={null} onSelect={(c) => c && vincularCliente(s.id, c)} label="" />
                            ) : (
                              <button className="btn-secondary btn-sm" onClick={() => setVinculando(s.id)}>Vincular a un cliente existente</button>
                            )}

                            <h4 style={{ margin: 'var(--spacing-3) 0', fontSize: '13px' }}>Paquete{(s.paquetes_generados || []).length > 1 ? 's' : ''}</h4>
                            {(s.paquetes_generados || []).length > 0 && (
                              <div style={{ display: 'grid', gap: '4px', marginBottom: 'var(--spacing-2)' }}>
                                {s.paquetes_generados.map((p) => (
                                  <div key={p.id} className="text-sm">
                                    <span className="mono">{p.tracking}</span>
                                    {' — '}
                                    <span style={{ color: 'var(--color-text-secondary)' }}>
                                      {p.estado_actual_detalle?.nombre || '—'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {s.estado === 'confirmada' ? (
                              <button className="btn-secondary btn-sm" onClick={() => setCreandoPaquete(s.id)}>
                                <Package size={14} /> {(s.paquetes_generados || []).length > 0 ? 'Agregar otro paquete' : 'Crear paquete'}
                              </button>
                            ) : (s.paquetes_generados || []).length === 0 && (
                              <p className="text-sm text-secondary">Se habilita al confirmar la solicitud.</p>
                            )}

                            <h4 style={{ margin: 'var(--spacing-3) 0', fontSize: '13px' }}>Notas internas</h4>
                            <textarea
                              rows={3} style={{ width: '100%' }}
                              value={notasTemp[s.id] ?? ''}
                              onChange={(e) => setNotasTemp((prev) => ({ ...prev, [s.id]: e.target.value }))}
                            />
                            <button className="btn-primary btn-sm" style={{ marginTop: 'var(--spacing-2)' }} onClick={() => guardarNotas(s.id)}>
                              Guardar notas
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {solicitudes.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 'var(--spacing-4)', gap: 'var(--spacing-2)' }}>
          <span className="text-sm text-secondary">
            {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, solicitudes.length)} de {solicitudes.length}
          </span>
          <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
            <ChevronLeft size={16} />
          </button>
          <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {mostrarImagen && (
        <CotizacionPuertaPuertaImagenModal
          solicitud={solicitudes.find((s) => s.id === mostrarImagen)}
          onClose={() => setMostrarImagen(null)}
          onEnviada={(actualizada) => setSolicitudes((prev) => prev.map((x) => (x.id === actualizada.id ? actualizada : x)))}
        />
      )}

      {creandoPaquete && (() => {
        const s = solicitudes.find((x) => x.id === creandoPaquete);
        if (!s) return null;
        return (
          <PaqueteFormModal
            prellenado={{
              cliente: s.cliente_detalle,
              origen: 'puerta-a-puerta',
              solicitudId: s.id,
              etiqueta: 'Puerta a Puerta',
              datos: { descripcion: s.contenido_descripcion || '' },
            }}
            onClose={() => setCreandoPaquete(null)}
            onGuardado={(solicitudActualizada) => {
              setSolicitudes((prev) => prev.map((x) => (x.id === solicitudActualizada.id ? solicitudActualizada : x)));
              setCreandoPaquete(null);
            }}
          />
        );
      })()}

      {creandoManual && (
        <NuevaSolicitudPuertaPuertaAdminModal
          onClose={() => setCreandoManual(false)}
          onCreada={() => {
            // La respuesta de creación usa el serializer "Crear" (los mismos
            // campos que el formulario público, sin id/estado/etc.) — se
            // recarga la lista completa en vez de intentar insertarla a mano.
            cargar();
            setCreandoManual(false);
          }}
        />
      )}
    </div>
  );
}
